<template>
  <v-card flat>
    <v-card-text class="pa-4">
      <div class="text-caption text-medium-emphasis mb-6">
        <v-icon small class="mr-1">mdi-information-outline</v-icon>
        Search for personal information updates. Upload supporting documents for
        verification.
      </div>
      <v-form
        ref="personalForm"
        class="compact-form"
        @submit.prevent="searchPersonal"
      >
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="personalSearch.identifier"
              :label="identifierLabel"
              dense
              outlined
              hide-details
              clearable
              maxlength="11"
              :rules="[rules.number]"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="12" md="3">
            <v-btn
              color="primary"
              type="submit"
              class="text-none"
              :disabled="!personalSearch.identifier"
              :loading="loading"
            >
              <v-icon left small>mdi-magnify</v-icon>
              Search
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <!-- Personal Information Update Form -->
      <v-form
        v-if="applicantData?.usn_id"
        @submit.prevent="submitPersonalUpdate"
        class="mt-6"
      >
        <v-card class="mb-4" elevation="0">
          <v-row>
            <v-col cols="12" md="4">
              <div class="d-flex align-center mb-2">
                <span class="text-body-2 font-weight-medium">Full Name</span>
                <v-chip
                  v-if="applicantData.name !== originalData?.name"
                  x-small
                  color="orange"
                  text-color="white"
                  class="ml-2"
                >
                  Modified
                </v-chip>
                <v-chip
                  v-if="applicantData.name !== originalData?.name && !nameProof"
                  x-small
                  color="red"
                  text-color="white"
                  class="ml-2"
                >
                  Attachment Required
                </v-chip>
              </div>
              <v-text-field
                v-model="applicantData.name"
                placeholder="Enter full name"
                dense
                outlined
                hide-details
                :rules="[(v) => !!v || 'Name is required']"
              >
                <template v-slot:append-outer>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        v-on="on"
                        @click="$refs.nameProofInput.click()"
                      >
                        <v-icon small>mdi-paperclip</v-icon>
                      </v-btn>
                    </template>
                    <span>Attach name change proof</span>
                  </v-tooltip>
                  <input
                    type="file"
                    ref="nameProofInput"
                    accept=".pdf,.jpg,.jpeg,.png"
                    style="display: none"
                    @change="handleNameProofUpload"
                  />
                </template>
              </v-text-field>

              <!-- Original Value -->
              <div
                v-if="originalData?.name"
                class="text-caption grey--text mt-1"
              >
                <v-icon x-small class="mr-1">mdi-history</v-icon>
                Original: {{ originalData.name }}
              </div>

              <!-- Proof Preview -->
              <div v-if="nameProof" class="mt-2">
                <v-chip
                  small
                  color="green"
                  text-color="white"
                  @click="viewProof('name')"
                  class="mr-2"
                >
                  <v-icon x-small left>mdi-file-check</v-icon>
                  Proof Uploaded
                </v-chip>
                <v-chip
                  small
                  color="red"
                  text-color="white"
                  @click="nameProof = null"
                >
                  <v-icon x-small left>mdi-close</v-icon>
                  Remove
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="d-flex align-center mb-2">
                <span class="text-body-2 font-weight-medium"
                  >Email Address</span
                >
                <v-chip
                  v-if="applicantData.email !== originalData?.email"
                  x-small
                  color="orange"
                  text-color="white"
                  class="ml-2"
                >
                  Modified
                </v-chip>
              </div>
              <v-text-field
                v-model="applicantData.email"
                placeholder="email@example.com"
                dense
                outlined
                hide-details
                :rules="[rules.required, rules.email]"
                type="email"
              >
              </v-text-field>
              <div
                v-if="originalData?.email"
                class="text-caption grey--text mt-1"
              >
                <v-icon x-small class="mr-1">mdi-history</v-icon>
                Original: {{ originalData.email }}
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="d-flex align-center mb-2">
                <span class="text-body-2 font-weight-medium">Gender</span>
                <v-chip
                  v-if="applicantData.gender !== originalData?.gender"
                  x-small
                  color="orange"
                  text-color="white"
                  class="ml-2"
                >
                  Modified
                </v-chip>
              </div>
              <v-radio-group
                v-model="applicantData.gender"
                row
                dense
                :rules="[rules.required]"
                class="pt-0"
              >
                <v-radio label="Male" value="Male" color="primary"></v-radio>
                <v-radio
                  label="Female"
                  value="Female"
                  color="primary"
                ></v-radio>
                <v-radio label="Other" value="Other" color="primary"></v-radio>
              </v-radio-group>
            </v-col>

            <v-col cols="12" md="4">
              <div class="d-flex align-center mb-2">
                <span class="text-body-2 font-weight-medium"
                  >Mobile Number </span
                ><v-chip
                  v-if="applicantData.mobile_no !== originalData?.mobile_no"
                  x-small
                  color="orange"
                  text-color="white"
                  class="ml-2"
                >
                  Modified
                </v-chip>
              </div>
              <v-text-field
                v-model="applicantData.mobile_no"
                placeholder="Enter 10-digit mobile number"
                dense
                outlined
                hide-details
                :rules="[rules.required, rules.mobile]"
                type="tel"
              >
              </v-text-field>

              <div
                v-if="originalData?.mobile_no"
                class="text-caption grey--text mt-1"
              >
                <v-icon x-small class="mr-1">mdi-history</v-icon>
                Original: {{ originalData.mobile_no }}
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="d-flex align-center mb-2">
                <span class="text-body-2 font-weight-medium"
                  >Date of Birth</span
                >
                <v-chip
                  v-if="applicantData.dob !== originalData?.dob"
                  x-small
                  color="orange"
                  text-color="white"
                  class="ml-2"
                >
                  Modified
                </v-chip>
                <v-chip
                  v-if="applicantData.dob !== originalData?.dob && !dobProof"
                  x-small
                  color="red"
                  text-color="white"
                  class="ml-2"
                >
                  Attachment Required
                </v-chip>
              </div>
              <v-menu
                v-model="menu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formattedDob"
                    dense
                    outlined
                    readonly
                    placeholder="DD-MM-YYYY"
                    v-bind="attrs"
                    v-on="on"
                    hide-details
                  >
                    <template v-slot:append-outer>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            icon
                            small
                            color="primary"
                            v-on="on"
                            @click="$refs.dobProofInput.click()"
                          >
                            <v-icon small>mdi-paperclip</v-icon>
                          </v-btn>
                        </template>
                        <span>Attach DOB proof</span>
                      </v-tooltip>

                      <input
                        type="file"
                        ref="dobProofInput"
                        accept=".pdf,.jpg,.jpeg,.png"
                        style="display: none"
                        @change="handleDobProofUpload"
                      />
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="applicantData.dob"
                  @input="menu = false"
                  no-title
                  scrollable
                  ref="dobPicker"
                  :min="hundredYearsAgo"
                  :max="sixteenYearsAgo"
                ></v-date-picker>
              </v-menu>

              <div
                v-if="originalData?.dob"
                class="text-caption grey--text mt-1"
              >
                <v-icon x-small class="mr-1">mdi-history</v-icon>
                Original: {{ formatDate(originalData.dob) }}
              </div>
              <div v-if="dobProof" class="mt-2">
                <v-chip
                  small
                  color="green"
                  text-color="white"
                  @click="viewProof('dob')"
                  class="mr-2"
                >
                  <v-icon x-small left>mdi-file-check</v-icon>
                  Proof Uploaded
                </v-chip>
                <v-chip
                  small
                  color="red"
                  text-color="white"
                  @click="dobProof = null"
                >
                  <v-icon x-small left>mdi-close</v-icon>
                  Remove
                </v-chip>
              </div>
            </v-col>

            <v-col cols="12" md="4">
              <div class="d-flex align-center mb-2">
                <span class="text-body-2 font-weight-medium"
                  >SAP/Employee ID</span
                >
                <v-chip
                  v-if="applicantData.employee_id !== originalData?.employee_id"
                  x-small
                  color="orange"
                  text-color="white"
                  class="ml-2"
                >
                  Modified
                </v-chip>
                <v-chip
                  v-if="
                    applicantData.employee_id !== originalData?.employee_id &&
                    !employeeIdProof
                  "
                  x-small
                  color="red"
                  text-color="white"
                  class="ml-2"
                >
                  Attachment Required
                </v-chip>
              </div>
              <v-text-field
                v-model="applicantData.employee_id"
                placeholder="Enter SAP/Employee ID"
                dense
                outlined
                hide-details
              >
                <template v-slot:append-outer>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        v-on="on"
                        @click="$refs.employeeIdProofInput.click()"
                      >
                        <v-icon small>mdi-paperclip</v-icon>
                      </v-btn>
                    </template>
                    <span>Attach SAP/Employee ID proof</span>
                  </v-tooltip>
                  <input
                    type="file"
                    ref="employeeIdProofInput"
                    accept=".pdf,.jpg,.jpeg,.png"
                    style="display: none"
                    @change="handleEmployeeIdProofUpload"
                  />
                </template>
              </v-text-field>

              <div
                v-if="originalData?.employee_id"
                class="text-caption grey--text mt-1"
              >
                <v-icon x-small class="mr-1">mdi-history</v-icon>
                Original: {{ originalData.employee_id }}
              </div>

              <!-- Proof Preview -->
              <div v-if="employeeIdProof" class="mt-2">
                <v-chip
                  small
                  color="green"
                  text-color="white"
                  @click="viewProof('employeeId')"
                  class="mr-2"
                >
                  <v-icon x-small left>mdi-file-check</v-icon>
                  Proof Uploaded
                </v-chip>
                <v-chip
                  small
                  color="red"
                  text-color="white"
                  @click="employeeIdProof = null"
                >
                  <v-icon x-small left>mdi-close</v-icon>
                  Remove
                </v-chip>
              </div>
            </v-col>

            <!-- Gender -->
          </v-row>

          <!-- Communication Address -->
          <v-row>
            <v-col cols="12">
              <div class="d-flex align-center mb-2">
                <span class="text-body-2 font-weight-medium"
                  >Communication Address</span
                >
                <v-chip
                  v-if="
                    JSON.stringify(applicantData.current_address) !==
                    JSON.stringify(originalData?.current_address)
                  "
                  x-small
                  color="orange"
                  text-color="white"
                  class="ml-2"
                >
                  Modified
                </v-chip>
              </div>

              <v-row>
                <v-col cols="12" md="4">
                  <label class="form-label"
                    >Address Line 1 <span class="error--text">* </span></label
                  >
                  <v-text-field
                    v-model="applicantData.current_address.address_line1"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <label class="form-label">Address Line 2</label>
                  <v-text-field
                    v-model="applicantData.current_address.address_line2"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <label class="form-label"
                    >Country <span class="error--text">* </span></label
                  >
                  <v-autocomplete
                    v-model="applicantData.current_address.country"
                    hide-details="auto"
                    outlined
                    dense
                    :items="countries"
                    item-text="name"
                    item-value="name"
                    :readonly="applicantData.current_address.is_indian_address"
                    :append-icon="
                      applicantData.current_address.is_indian_address
                        ? ''
                        : '$dropdown'
                    "
                    attach
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="4">
                  <label class="form-label"
                    >{{
                      applicantData.current_address.is_indian_address
                        ? "State"
                        : "State/Province"
                    }}
                    <span class="error--text">* </span></label
                  >
                  <v-text-field
                    v-model="applicantData.current_address.state"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <label class="form-label"
                    >{{
                      applicantData.current_address.is_indian_address
                        ? "City"
                        : "City/Town"
                    }}
                    <span class="error--text">* </span></label
                  >
                  <v-text-field
                    v-model="applicantData.current_address.city"
                    hide-details="auto"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <label class="form-label"
                    >Postcode <span class="error--text">* </span></label
                  >
                  <v-text-field
                    v-model="applicantData.current_address.postcode"
                    outlined
                    dense
                    maxlength="6"
                    hide-details="auto"
                    type="email"
                    @change="
                      applicantData.current_address.postcode =
                        applicantData.current_address.postcode.toUpperCase()
                    "
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <label class="form-label"
                    >Locality <span class="error--text">* </span></label
                  >
                  <v-radio-group
                    v-model="applicantData.current_address.locality"
                    row
                    dense
                    class="pt-0"
                    hide-details="auto"
                  >
                    <v-radio label="Urban" value="urban"></v-radio>
                    <v-radio label="Rural" value="rural"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>

              <!-- Address Proof Upload -->
              <div class="mt-4">
                <v-card outlined class="pa-3">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        Address Proof Document
                      </div>
                      <div class="text-caption grey--text">
                        Upload Aadhar Card / Passport / Driving License
                      </div>
                    </div>

                    <div>
                      <v-btn
                        color="primary"
                        outlined
                        small
                        @click="$refs.addressProofInput.click()"
                        class="text-none"
                      >
                        <v-icon x-small left>mdi-file-upload</v-icon>
                        Upload Proof
                      </v-btn>
                      <input
                        type="file"
                        ref="addressProofInput"
                        accept=".pdf,.jpg,.jpeg,.png"
                        style="display: none"
                        @change="handleAddressProofUpload"
                      />
                    </div>
                  </div>

                  <!-- Proof Preview -->
                  <v-card
                    v-if="addressProof"
                    class="mt-3 pa-3"
                    outlined
                    style="background-color: #f9f9f9"
                  >
                    <v-row align="center">
                      <v-col cols="auto">
                        <v-icon color="success">mdi-file-check</v-icon>
                      </v-col>
                      <v-col>
                        <div class="text-body-2">{{ addressProof.name }}</div>
                        <div class="text-caption grey--text">
                          {{ (addressProof.size / 1024).toFixed(1) }} KB •
                          {{ addressProof.type }}
                        </div>
                      </v-col>
                      <v-col cols="auto">
                        <v-btn
                          icon
                          small
                          @click="viewProof('address')"
                          color="primary"
                        >
                          <v-icon small>mdi-eye</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="addressProof = null"
                          color="error"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-card>
              </div>
            </v-col>
          </v-row>

          <!-- Summary Card -->
          <v-card
            v-if="hasChanges"
            class="mt-4 mb-4"
            color="blue-lighten-5"
            flat
          >
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="blue" class="mr-2"
                  >mdi-alert-circle-outline</v-icon
                >
                <div>
                  <div class="text-body-2 font-weight-medium">
                    Review Required
                  </div>
                  <div class="text-caption">
                    {{ modifiedFields.length }} field(s) modified.
                    {{ pendingProofs }} proof(s) pending. Please verify all
                    changes before submission.
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Action Buttons -->
          <div class="d-flex justify-end mt-6">
            <v-btn
              color="primary"
              type="submit"
              :disabled="!hasChanges || hasMissingProofs"
              :loading="submitting"
              class="text-none"
            >
              <v-icon left small>mdi-check-circle</v-icon>
              {{ submitting ? "Submitting..." : "Submit" }}
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { rules } from "@/helpers/rules";
export default {
  data() {
    return {
      rules,
      personalSearch: {
        filterBy: "USN",
        identifier: "",
      },
      applicantData: {},
      personalFilters: ["USN"],
      nameProof: null,
      addressProof: null,
      dobProof: null,
      employeeIdProof: null,
      originalData: null,
      submitting: false,
      menu: false,
      countries: [],
      loading: false,
    };
  },

  computed: {
    identifierLabel() {
      if (!this.personalSearch.filterBy) return "Select filter first";
      return this.personalSearch.filterBy;
    },
    formattedDob() {
      return this.applicantData.dob
        ? this.formatDate(this.applicantData.dob)
        : "";
    },

    hasChanges() {
      if (!this.originalData) return false;

      const fields = [
        "name",
        "email",
        "mobile_no",
        "dob",
        "gender",
        "employee_id",
        "current_address",
        "current_address.state",
      ];
      return fields.some((field) => {
        if (field === "current_address") {
          return (
            JSON.stringify(this.applicantData[field]) !==
            JSON.stringify(this.originalData[field])
          );
        }
        return this.applicantData[field] !== this.originalData[field];
      });
    },

    modifiedFields() {
      if (!this.originalData) return [];

      const fields = [];
      if (this.applicantData.name !== this.originalData.name)
        fields.push("Name");
      if (this.applicantData.dob !== this.originalData.dob) fields.push("DOB");
      if (this.applicantData.gender !== this.originalData.gender)
        fields.push("Gender");
      if (this.applicantData.mobile_no !== this.originalData.mobile_no)
        fields.push("MobileNumber");
      if (this.applicantData.employee_id !== this.originalData.employee_id)
        fields.push("EmployeeId");
      if (
        JSON.stringify(this.applicantData.current_address) !==
        JSON.stringify(this.originalData.current_address)
      )
        fields.push("Address");

      console.log(fields);
      return fields;
    },

    pendingProofs() {
      let count = 0;
      if (
        this.applicantData.name !== this.originalData?.name &&
        !this.nameProof
      )
        count++;
      if (
        JSON.stringify(this.applicantData.current_address) !==
          JSON.stringify(this.originalData?.current_address) &&
        !this.addressProof
      )
        count++;

      if (
        JSON.stringify(this.applicantData.dob) !==
          JSON.stringify(this.originalData?.dob) &&
        !this.dobProof
      )
        count++;

      if (
        this.applicantData.employee_id !== this.originalData?.employee_id &&
        !this.employeeIdProof
      )
        count++;

      return count;
    },

    hasMissingProofs() {
      return this.pendingProofs > 0;
    },
    sixteenYearsAgo() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 16);
      date.setMonth(11);
      date.setDate(31);
      return date.toISOString().substr(0, 10);
    },
    hundredYearsAgo() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 100);
      date.setMonth(0);
      date.setDate(1);
      return date.toISOString().substr(0, 10);
    },
  },
  created() {
    this.getCountries();
  },
  watch: {
    menu(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.dobPicker) {
            this.$refs.dobPicker.activePicker = "YEAR";
          }
        });
      }
    },
  },
  methods: {
    async getCountries() {
      try {
        const response = await this.$axios.get("/countries");
        this.countries = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async searchPersonal() {
      this.applicantData = {};
      try {
        this.loading = true;
        console.log("Searching Personal Updates:", this.personalSearch);
        const res = await this.$axios.get(
          `/get_student_info_by_usn_id/${this.personalSearch.identifier}`
        );

        this.applicantData = res.data.data;
        this.originalData = JSON.parse(JSON.stringify(this.applicantData));
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: error.response.data.error,
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    handleNameProofUpload(event) {
      this.nameProof = event.target.files[0];
      event.target.value = "";
    },

    handleCertificateProofUpload(event) {
      this.certificateNameProof = event.target.files[0];
      event.target.value = "";
    },

    handleEmailProofUpload(event) {
      this.emailProof = event.target.files[0];
      event.target.value = "";
    },

    handleMobileProofUpload(event) {
      this.mobileProof = event.target.files[0];
      event.target.value = "";
    },

    handleDobProofUpload(event) {
      this.dobProof = event.target.files[0];
      event.target.value = "";
    },

    handleGenderProofUpload(event) {
      this.genderProof = event.target.files[0];
      event.target.value = "";
    },

    handleEmployeeIdProofUpload(event) {
      this.employeeIdProof = event.target.files[0];
      event.target.value = "";
    },

    handleAddressProofUpload(event) {
      this.addressProof = event.target.files[0];
      event.target.value = "";
    },

    viewProof(type) {
      const proof = this[`${type}Proof`];
      if (proof) {
        const url = URL.createObjectURL(proof);
        window.open(url, "_blank");
      }
    },

    formatDate(date) {
      if (!date) return "";
      const [year, month, day] = date.split("-");
      return `${day}-${month}-${year}`;
    },

    async submitPersonalUpdate() {
      if (!this.$refs.personalForm.validate()) return;
      this.submitting = true;

      try {
        const formData = new FormData();

        formData.append("applicantData", JSON.stringify(this.applicantData));
        const proofs = [
          { key: "nameProof", file: this.nameProof },
          { key: "addressProof", file: this.addressProof },
          { key: "dobProof", file: this.dobProof },
          { key: "employeeIdProof", file: this.employeeIdProof },
        ];

        proofs.forEach(({ key, file }) => {
          if (file) {
            formData.append(key, file);
          }
        });
        var addr = this.applicantData.current_address || {};
        var orig =
          (this.originalData && this.originalData.current_address) || {};

        var changed = Object.keys(Object.assign({}, addr, orig)).some(function (
          key
        ) {
          return (addr[key] || null) !== (orig[key] || null);
        });

        if (changed) {
          var normalized = {};
          Object.keys(addr).forEach(function (key) {
            normalized[key] = addr[key] || null;
          });
          this.applicantData.current_address = normalized;
        } else {
          delete this.applicantData.current_address;
        }

        const response = await this.$axios.put(
          `/student_info/${this.applicantData.id}`,
          {
            student_info: this.applicantData,
          }
        );

        await this.uploadAttachments();

        this.applicantData = {};
        this.$store.commit("showSnackbar", {
          message: "Updated Successfully",
          color: "success",
        });
      } catch (error) {
        const errors = error?.response?.data?.errors;

        if (!errors || typeof errors !== "object") {
          this.$store.commit("showSnackbar", {
            message: "Validation failed",
            color: "error",
          });
          return;
        }

        const [parentKey, parentValue] = Object.entries(errors)[0];

        if (typeof parentValue === "object") {
          const [childKey, messages] = Object.entries(parentValue)[0];

          this.$store.commit("showSnackbar", {
            message: `${this.formatKey(parentKey)} ${this.formatKey(
              childKey
            )} ${messages[0]}`,
            color: "error",
          });
        }
      } finally {
        this.submitting = false;
      }
    },

    formatKey(key) {
      return key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
    },
    async uploadAttachments() {
      const formData = new FormData();
      const proofsToUpload = [];

      if (
        this.applicantData.name !== this.originalData?.name &&
        this.nameProof
      ) {
        proofsToUpload.push({ key: "name_proof", file: this.nameProof });
      }

      if (this.applicantData.dob !== this.originalData?.dob && this.dobProof) {
        proofsToUpload.push({ key: "dob_proof", file: this.dobProof });
      }

      if (
        this.applicantData.employee_id !== this.originalData?.employee_id &&
        this.employeeIdProof
      ) {
        proofsToUpload.push({
          key: "employee_id_proof",
          file: this.employeeIdProof,
        });
      }

      if (
        this.applicantData.current_address !==
          this.originalData?.current_address &&
        this.addressProof
      ) {
        proofsToUpload.push({ key: "address_proof", file: this.addressProof });
      }

      if (proofsToUpload.length === 0) return;

      proofsToUpload.forEach(({ key, file }) => {
        formData.append(key, file);
      });

      this.uploading = true;

      try {
        await this.$axios.put(
          `/upload_proofs/${this.personalSearch.identifier}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        this.$store.commit("showSnackbar", {
          message: "Uploaded successfully",
          color: "success",
        });
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>
